import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'react-toastify';
import VideoPreviewModal from '../components/VideoPreviewModal';
import VideoCard from '../components/VideoCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All videos');
  const [sortOrder, setSortOrder] = useState('newest');
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch videos from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch videos
        const videosResponse = await axios.get(`${API}/videos`);
        setVideos(videosResponse.data);
        
        // Fetch categories
        const categoriesResponse = await axios.get(`${API}/categories`);
        setCategories(categoriesResponse.data);
        
        // Fetch settings
        const settingsResponse = await axios.get(`${API}/settings`);
        setSettings(settingsResponse.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: "Error",
          description: "Failed to load videos. Please try again later.",
          variant: "destructive"
        });
        setLoading(false);
      }
    };

    fetchData();
    // Mount-only effect: all referenced values (API, axios, toast) are module-stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredVideos = useMemo(() => {
    let filtered = videos;

    // Filter by category (skip if "All videos")
    if (activeCategory && activeCategory !== 'All videos') {
      filtered = filtered.filter(video => 
        video.tags && video.tags.some(tag => tag.trim() === activeCategory)
      );
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(video =>
        video.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.videoCode?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort by createdAt
    filtered = [...filtered].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortOrder === 'oldest' ? ta - tb : tb - ta;
    });

    return filtered;
  }, [videos, searchQuery, activeCategory, sortOrder]);

  const handleCopyCode = (code) => {
    // Universal copy method that works in all environments
    const copyToClipboard = (text) => {
      // Create temporary textarea
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "absolute";
      textArea.style.opacity = "0";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      
      try {
        textArea.select();
        textArea.setSelectionRange(0, 99999); // For mobile devices
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success(`✓ Code ${code} copied!`, {
            position: "bottom-center",
            autoClose: 2000,
            hideProgressBar: true,
            closeButton: false,
            style: {
              background: '#10b981',
              color: 'white',
              borderRadius: '9999px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
            }
          });
        } else {
          throw new Error('Copy command failed');
        }
      } catch (err) {
        document.body.removeChild(textArea);
        console.error('Copy failed:', err);
        toast.error(`Failed to copy. Code: ${code}`, {
          position: "bottom-center",
          autoClose: 4000,
        });
      }
    };

    copyToClipboard(code);
  };

  const handleCategoryClick = (categoryName) => {
    setActiveCategory(categoryName);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePlayPreview = (video) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <div className="min-h-screen bg-[#faf6f2]">
      {/* Header */}
      <header className="py-8 md:py-12 text-center border-b border-gray-200">
        {settings?.headerLogo && (
          <img
            src={settings.headerLogo}
            alt=""
            fetchPriority="high"
            decoding="async"
            width="320"
            height="96"
            className="mx-auto max-h-16 md:max-h-24 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </header>

      <main>

      {/* Intro Text */}
      <section className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12 text-center">
        <h2 className="text-base md:text-lg font-bold uppercase mb-3 md:mb-4 text-gray-900">
          {settings?.introHeadline || "ACCESS ALL OUR PREVIOUSLY RELEASED EXCLUSIVE VIDEOS! 🔓"}
        </h2>
        <p className="text-sm md:text-base text-gray-700 leading-relaxed">
          {settings?.introText || "We are a young couple that loves to have sex... and to share it 😋 But you know this by now! Here you can access all our previously released exclusive videos! DM me the code of the sextape you want to see and I'll send it instantly! 🥰❤️"}
        </p>
      </section>

      {/* Search Bar */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mb-6 md:mb-8">
        <div className="relative w-full">
          <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 md:w-5 md:h-5" />
          <Input
            type="text"
            placeholder="🔍 SEARCH..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 md:pl-12 py-5 md:py-6 rounded-full border-gray-300 bg-white w-full text-sm md:text-base"
          />
        </div>
      </section>

      {/* Category Tabs */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mb-6 md:mb-8">
        <div className="flex gap-2 md:gap-3 flex-wrap">
          {categories.map((category) => (
            <button
              key={category.name}
              onClick={() => setActiveCategory(category.name)}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-xs md:text-sm font-medium transition-all ${
                activeCategory === category.name
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              {category.name} <Badge variant="secondary" className="ml-1 md:ml-2">{category.count}</Badge>
            </button>
          ))}
        </div>
      </section>

      {/* Section Heading + Sort */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mb-6 md:mb-8">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            {activeCategory} 🔖{filteredVideos.length}
          </h2>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="appearance-none bg-white border border-gray-300 rounded-full pl-4 md:pl-5 py-2 md:py-2.5 pr-10 text-xs md:text-sm font-medium text-gray-700 cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            aria-label="Sort videos"
            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\'%3E%3Cpath fill=\'%23374151\' d=\'M0 0l5 6 5-6z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.875rem center' }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
      </section>

      {/* Video Grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-12 md:pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {filteredVideos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onCopyCode={handleCopyCode}
              onPlayPreview={handlePlayPreview}
              buttonIcon={settings?.buttonIcon || ''}
              ctaButtonLink={settings?.ctaButtonLink || 'https://onlyfans.com/my/chats/chat/960542/'}
            />
          ))}
        </div>
      </section>

      {/* Footer */}
      </main>
      <footer className="bg-gray-900 text-white py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col gap-6 md:grid md:grid-cols-3 md:gap-8 md:items-center">
            {/* Left: Logo */}
            <div className="text-center md:text-left">
              {settings?.footerLogo && (
                <img
                  src={settings.footerLogo}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width="200"
                  height="48"
                  className="max-h-10 md:max-h-12 object-contain mx-auto md:mx-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>

            {/* Center: Social Icons */}
            <div className="flex justify-center gap-4 md:gap-6">
              {settings?.footerLinks && settings.footerLinks.map((link) => {
                // Skip links with no URL or no custom icon uploaded
                if (!link.url || link.url === '#') return null;
                if (!link.iconUrl) return null;

                return (
                  <a
                    key={link.icon || link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group transition-all opacity-100 hover:opacity-70"
                    aria-label={link.name}
                  >
                    <img
                      src={link.iconUrl}
                      alt={link.name}
                      loading="lazy"
                      decoding="async"
                      width="24"
                      height="24"
                      className="w-6 h-6 transition-opacity"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </a>
                );
              })}
            </div>

            {/* Right: Category Pills - Now in Row on Mobile */}
            <div className="flex justify-center md:justify-end gap-2 md:gap-3 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryClick(cat.name)}
                  className="px-3 py-1.5 md:px-4 md:py-2 bg-gray-800 hover:bg-gray-700 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name} {cat.count}
                </button>
              ))}
            </div>
          </div>
        </div>
      </footer>
      
      {/* Video Preview Modal */}
      <VideoPreviewModal
        video={selectedVideo}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default LandingPage;
